import React, { useCallback, useRef } from 'react';
import {
  Dialog as DialogComponent,
  DialogTitle as DialogTitleComponent,
  DialogPanel as DialogPanelComponent,
} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

function useAnimatedHeight() {
  const prevHeight = useRef(null);
  const isAnimating = useRef(false);
  const cleanupRef = useRef(null);

  const containerRef = useCallback(container => {
    // Clean up previous observer
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    if (!container) {
      prevHeight.current = null;
      isAnimating.current = false;
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      if (isAnimating.current) return;

      const newHeight = Math.round(entry.borderBoxSize?.[0]?.blockSize ?? container.offsetHeight);

      // First observation — just store the height
      if (prevHeight.current === null) {
        prevHeight.current = newHeight;
        return;
      }

      const oldHeight = prevHeight.current;
      prevHeight.current = newHeight;

      if (oldHeight === newHeight) return;

      isAnimating.current = true;

      // Snap to old height with no transition
      container.style.overflow = 'hidden';
      container.style.transition = 'none';
      container.style.height = `${oldHeight}px`;
      container.offsetHeight; // Force reflow

      // Animate to new height
      container.style.transition = 'height 250ms cubic-bezier(0.25, 1, 0.5, 1)';
      container.style.height = `${newHeight}px`;
    });

    observer.observe(container);

    const handleTransitionEnd = e => {
      if (e.target === container && e.propertyName === 'height') {
        isAnimating.current = false;
        container.style.transition = '';
        container.style.height = '';
        container.style.overflow = '';
        prevHeight.current = container.offsetHeight;
      }
    };

    container.addEventListener('transitionend', handleTransitionEnd);

    cleanupRef.current = () => {
      observer.disconnect();
      container.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, []);

  return containerRef;
}

const Dialog = ({ isOpen = false, onClose = null, title, size = 'lg', children }) => {
  const containerRef = useAnimatedHeight();

  return (
    <DialogComponent
      open={isOpen}
      onClose={onClose ? onClose : () => null}
      className="fixed z-50 inset-0 overflow-y-auto"
      unmount={true}
    >
      <div className="fixed inset-0 bg-black/80" aria-hidden="true" />

      <div className="flex items-center justify-center min-h-screen p-6 h-full">
        <div
          ref={containerRef}
          className={clsx(
            'relative bg-white dark:bg-background dark:border dark:border-border rounded-2xl w-full justify-center mx-auto py-6 px-6 flex flex-col max-h-full overflow-auto',
            {
              'max-w-lg': size === 'lg',
              'max-w-xl': size === 'xl',
              'max-w-2xl': size === '2xl',
              'max-w-3xl': size === '3xl',
            },
          )}
        >
          <DialogPanelComponent className="h-full flex-1 flex flex-col overflow-auto px-1">
            {title && <DialogTitleComponent className="text-lg mb-4">{title}</DialogTitleComponent>}

            {children}
          </DialogPanelComponent>

          {onClose && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-foreground hover:text-muted-foreground transition-colors focus:outline-none"
            >
              <XMarkIcon width="24px" height="24px" className="fill-current" />
            </button>
          )}
        </div>
      </div>
    </DialogComponent>
  );
};

export default Dialog;
